import React from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import Image from '@/components/image'
import RichText, {hasRichText} from '@/components/rich-text'
import {Text} from '@/components/text'
import {Video} from '@/components/video'
import {BackgroundWrapper, Layout, Section} from '@/styles/grid'
import {tokens} from '@/theme'
import {isValidBackgroundColor} from '@/utils/is-valid-background-color'
import {isValidFontColor} from '@/utils/is-valid-font-color'
import mq from '@/utils/media-query'

import type {Props as RichTextProps} from '@/components/rich-text'
import type {ImageSource} from '@/types/image'
import type {InlineVideo} from '@/types/inline-video'
import type {BackgroundColor} from '@/utils/is-valid-background-color'
import type {FontColor} from '@/utils/is-valid-font-color'

export interface Props {
    image?: ImageSource
    video?: InlineVideo
    backgroundColor: BackgroundColor
    isMediaOnTheRight: boolean
    subtitle?: string
    subtitleColor: FontColor
    title?: string
    content?: RichTextProps['content']
    CTA?: React.ComponentType<any>
}

export const FeaturePanel = React.forwardRef<HTMLDivElement, Props>(
    (
        {
            image,
            video,
            backgroundColor,
            isMediaOnTheRight,
            subtitle,
            subtitleColor,
            title,
            content,
            CTA,
        },
        ref,
    ) => (
        <BackgroundWrapper
            ref={ref}
            $color={isValidBackgroundColor(backgroundColor) ? backgroundColor : 'default'}
        >
            <Section>
                <Layout
                    $variant="two-asymmetrical"
                    $alignItems="center"
                    $mirrored={!isMediaOnTheRight}
                >
                    <div>
                        {subtitle && (
                            <Text
                                $variant="paragraph-large"
                                $color={isValidFontColor(subtitleColor) ? subtitleColor : 'muted'}
                                style={{marginBottom: tokens.spacing6}}
                            >
                                {subtitle}
                            </Text>
                        )}

                        {title && (
                            <Text $variant="heading-base" style={{marginBottom: tokens.spacing24}}>
                                {title}
                            </Text>
                        )}

                        {hasRichText(content) && <RichText content={content} $color="muted" />}

                        {CTA && <StyledButton forwardedAs={CTA} $variant="outline" />}
                    </div>

                    <div>
                        {video && (
                            <Video
                                poster={image?.url}
                                video={video}
                                posterWidth={550}
                                dimensions={image?.dimensions}
                            />
                        )}

                        {!video && image && <Image image={image} width={600} />}
                    </div>
                </Layout>
            </Section>
        </BackgroundWrapper>
    ),
)

const StyledButton = styled(Button)`
    margin-top: ${tokens.spacing24};

    ${mq.max('sm')} {
        width: 100%;
    }
`
